<template>
    <div class="cttp-block">
        <div class="flex flex-wrap _border-b _border-gray-400 mt-0 pb-1 mb-2 md:mb-0">
            <dt-flip-charts-button class="ml-auto xs:mt-3 print:hidden"></dt-flip-charts-button>
        </div>

        <div class="border-t-2 border-blue-600 mt-2">
            <div class="table-header flex flex-wrap ">
                <div class="w-full sm:w-3/4">
                    <select-c
                        v-model="period"
                        :options="periods"
                        label="Period:"
                        wrapper-class="xs:w-full"
                        :disabled="isGetData && period == 'custom'"
                    />
                    <div class="inline-block" v-if="isJim() && period == 'custom'">
                        <div class="form-group-addon form-group-addon-sm ml-4 !py-0 _-mt-1">
                            <span class="input-addon-item bg-gray-100 font-bold">Enter period in minute(s):</span>
                            <input id="minutes"
                                v-model.number="inputMinutes"
                                @blur="changeMinutes"
                                placeholder="mins."
                                type="number"
                                min="1"
                                class="form-control-sm form-input-inline-w-none form-input-sm-inline input-addon-field w-16"
                                :disabled="isGetData"
                            />
                            <button class="input-addon-item btn-yellow text-white font-bold" @click="changeMinutes" :disabled="isGetData">Get Data</button>
                            <spinner v-if="isGetData" class="get-cttp ml-2"></spinner>
                        </div>
                    </div>
                </div>
                <dt-show-triggered-markets class="ml-auto _mt-1 _xs:mt-1" v-if="period!='custom'"></dt-show-triggered-markets>
            </div>
        </div>

        <data-table v-if="loadTable" :default-request="request" :default-columns="columns"></data-table>

        <flash-message secondsToDisplay="3" ref="flash"></flash-message>
    </div>
</template>

<script setup>
import { onMounted, computed, ref, inject } from 'vue';
import { useLocationHash } from '../../mixins/LocationHash';
import { useIsJim } from '../../mixins/IsJim';

const { type, defaultColumns } = defineProps(['type', 'defaultColumns']);

const eventBus = inject('eventBus');

const { searchHash } = useLocationHash();
const { isJim } = useIsJim();

const flash = ref(null);
const period = ref(searchHash('period') || 'intraday');
const inputMinutes = ref(searchHash('minutes') || 60);
const minutes = ref(searchHash('minutes') || 60);
const loadTable = ref(true);
const isGetData = ref(false);

const periods = [
    { label: 'Intraday', value: 'intraday', default: period.value === 'intraday' },
    { label: 'End-of-Day', value: 'eod', default: period.value === 'eod' },
];

if (isJim()) {
    periods.push({ label: 'Custom', value: 'custom', default: period.value === 'custom' });
}

const groupBy = ['category', 'symbolName'];
// const groupBy = ['uscCategory', 'symbolName'];

const columns = computed(() => {
    if (period.value === 'custom') {
        return defaultColumns.custom;
    } else if (period.value === 'eod') {
        return defaultColumns.eod;
    } else {
        return defaultColumns.intraday;
    }
});

const request = computed(() => {
    return {
        period: period.value,
        minutes: minutes.value,
        type,
        groupBy,
        route: window.route('api.cttp'),
    };
});

const validateMinutes = () => {
    if (!inputMinutes.value || inputMinutes.value.length === 0 || isNaN(parseInt(inputMinutes.value))) {
        inputMinutes.value = null;
        return false;
    }
    return true;
};

const changeMinutes = () => {
    if (!validateMinutes()) {
        return;
    }

    if (minutes.value !== inputMinutes.value) {
        minutes.value = inputMinutes.value;
        isGetData.value = true;
    } else {
        loadDataOneByOne();
    }
};

const loadDataOneByOne = async () => {
    isGetData.value = true;

    const route = window.route('api.cttp-symbol');
    const table = document.querySelector('.cttp-block .data-table');
    const rows = table.querySelectorAll('tbody td[data-type=Handbrake]');
    const minutesValue = minutes.value;

    for (let i = 0; i < rows.length; i++) {
        rows[i].innerHTML = '⟳';
        const symbol = rows[i].parentElement.querySelector('td[data-type=Symbol] div').innerText;
        const params = {
            symbol,
            minutes: minutesValue,
        };

        try {
            const response = await axios.post(route, params, { timeout: 30000 });
            rows[i].innerHTML = response.data.handbrake;
            const smacdTimeDiv = rows[i].parentElement.querySelector('td[data-type=SMACDTime] div');
            smacdTimeDiv.innerHTML = response.data.SMACDTime;

            const signalDiv = rows[i].parentElement.querySelector('td[data-type=Signal] div div');
            if (signalDiv) {
                const rawSignalValue = signalDiv.getAttribute('rawSignalValue');
                if (rawSignalValue && Number(rawSignalValue)) {
                    let smacd_signal = 'N/A';
                    if (response.data.rawValue >= 0) {
                        smacd_signal = Number(rawSignalValue) >= 0 ? 'G' : 'C';
                    }
                    if (response.data.rawValue <= 0) {
                        smacd_signal = Number(rawSignalValue) <= 0 ? 'R' : 'Y';
                    }

                    let signal = 'N/A';
                    if (smacd_signal === 'G') {
                        signal = 'BUY';
                    } else if (smacd_signal === 'C') {
                        signal = 'SELL -> BUY';
                    } else if (smacd_signal === 'R') {
                        signal = 'SELL';
                    } else if (smacd_signal === 'Y') {
                        signal = 'BUY -> SELL';
                    }

                    signalDiv.innerHTML = signal;

                    const cls = smacd_signal === 'G'
                        ? ['text-white', 'bg-green-500', 'px-2', 'py-px', 'text-xs', 'text-center']
                        : smacd_signal === 'Y'
                            ? ['text-black', 'bg-[#ffc107]', 'px-2', 'py-px', 'text-xs', 'text-center']
                            : smacd_signal === 'R'
                                ? ['text-white', 'bg-red-500', 'px-2', 'py-px', 'text-xs', 'text-center']
                                : smacd_signal === 'C'
                                    ? ['text-white', 'bg-teal-500', 'px-2', 'py-px', 'text-xs', 'text-center']
                                    : ['text-unch'];

                    signalDiv.classList.add(...cls);

                    if (smacd_signal === 'Y' || smacd_signal === 'C') {
                        rows[i].parentElement.classList.add('highlight');
                    }
                }
            }

            if (response.data.highlight) {
                rows[i].parentElement.classList.add('highlight');
            }
        } catch (error) {
            flash.value.error(`Error getting the CTTP data for symbol: ${symbol}`);
            rows[i].innerHTML = 'Error';
        }
    }

    isGetData.value = false;
};

onMounted(() => {
    if (period.value === 'custom') {
        setTimeout(() => {
            loadDataOneByOne();
        }, 1000);
    }

    eventBus.on('data-loaded', () => {
        if (period.value === 'custom') {
            setTimeout(() => {
                loadDataOneByOne();
            }, 1000);
        }
    });
});
</script>

<style lang="scss">
.cttp-block {
    .spinner.get-cttp {
        width: 24px;
        height: 24px;
        margin-top: auto;
        margin-bottom: auto;

        .spinner-child {
            background-color: #d38653 !important;
        }
    }

    .form-group-addon.form-group-addon-sm {
        margin-bottom: 0 !important;
        .input-addon-field,
        .input-addon-item {
            box-shadow: none !important;
            font-size: 0.815rem !important;
        }
    }
}
</style>
